import { useEffect, useMemo, useRef } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import shellJobService from '@/lib/shell-job.service';
import { awaitsAnswers, isActiveJob, jobPollInterval } from '@/lib/shell-job-status';
import type {
  CreateShellJobRequest,
  ShellJob,
  ShellJobItem,
  ShellJobTargetRequest,
} from '@/lib/shell-job.service';

export const shellJobKeys = {
  all: ['shell-jobs'] as const,
  list: (limit: number) => [...shellJobKeys.all, 'list', limit] as const,
  detail: (id: string) => [...shellJobKeys.all, 'detail', id] as const,
  items: (id: string) => [...shellJobKeys.all, 'items', id] as const,
  preview: (req: ShellJobTargetRequest | null) => [...shellJobKeys.all, 'preview', req] as const,
};

/** Accumulated item state for one job: items keyed by id plus the delta cursor. */
interface ItemsCache {
  byId: Record<string, ShellJobItem>;
  serverTime?: string;
}

export function useShellJobs(limit = 20) {
  return useQuery({
    queryKey: shellJobKeys.list(limit),
    queryFn: () => shellJobService.list(limit),
    // "Show more" changes the key; keep the shorter list until the longer one lands.
    placeholderData: keepPreviousData,
    // Keeps "running" rows in the recent list moving without a manual refresh, and
    // no-timeout runs' counts as their answers come in after they end.
    refetchInterval: (q) => (q.state.data?.some((j) => isActiveJob(j) || awaitsAnswers(j)) ? 5_000 : false),
    // Runs change while you look at one: coming back to the list fetches again
    // instead of showing a copy up to the global 5 min old until the next poll.
    staleTime: 0,
  });
}

/** Target preview; pass null to disable (e.g. an empty pasted list). */
export function useShellJobPreview(req: ShellJobTargetRequest | null) {
  return useQuery({
    queryKey: shellJobKeys.preview(req),
    queryFn: () => shellJobService.preview(req!),
    enabled: !!req,
    placeholderData: keepPreviousData,
    retry: false,
    // Online counts go stale fast: a reopened drawer fetches again instead of
    // showing (and gating Run on) an answer up to the global 5 min old.
    staleTime: 0,
  });
}

export function useShellJob(id: string) {
  return useQuery({
    queryKey: shellJobKeys.detail(id),
    queryFn: () => shellJobService.get(id),
    enabled: !!id,
    refetchInterval: (q) => (q.state.data ? jobPollInterval(q.state.data) : false),
  });
}

/**
 * Items of a job, fetched incrementally: the first call loads everything,
 * later calls pass the previous server_time as updated_after and merge the
 * delta into the cached map. Polls every `interval` ms (false = not at all).
 */
export function useShellJobItems(id: string, interval: number | false) {
  const polling = interval !== false;
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: shellJobKeys.items(id),
    queryFn: async (): Promise<ItemsCache> => {
      const prev = queryClient.getQueryData<ItemsCache>(shellJobKeys.items(id));
      const res = await shellJobService.items(id, prev?.serverTime);
      const byId = { ...(prev?.byId ?? {}) };
      for (const item of res.items ?? []) byId[item.id] = item;
      return { byId, serverTime: res.server_time };
    },
    enabled: !!id,
    refetchInterval: interval,
  });

  // When the job leaves "running", take one last delta so the final item
  // updates that landed between two polls are not lost.
  const wasPolling = useRef(polling);
  const { refetch } = query;
  useEffect(() => {
    if (wasPolling.current && !polling) void refetch();
    wasPolling.current = polling;
  }, [polling, refetch]);

  const items = useMemo(() => Object.values(query.data?.byId ?? {}), [query.data]);
  return { ...query, items };
}

export function useCreateShellJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateShellJobRequest) => shellJobService.create(data),
    onSuccess: (job) => {
      queryClient.setQueryData(shellJobKeys.detail(job.id), job);
      queryClient.invalidateQueries({ queryKey: [...shellJobKeys.all, 'list'] });
    },
  });
}

export function useCancelShellJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => shellJobService.cancel(id),
    onSuccess: (job) => {
      queryClient.setQueryData<ShellJob>(shellJobKeys.detail(job.id), job);
      queryClient.invalidateQueries({ queryKey: shellJobKeys.items(job.id) });
      queryClient.invalidateQueries({ queryKey: [...shellJobKeys.all, 'list'] });
    },
  });
}

export function useRerunOfflineShellJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => shellJobService.rerunOffline(id),
    onSuccess: (job) => {
      queryClient.setQueryData(shellJobKeys.detail(job.id), job);
      if (job.parent_job_id) {
        queryClient.invalidateQueries({ queryKey: shellJobKeys.detail(job.parent_job_id) });
      }
      queryClient.invalidateQueries({ queryKey: [...shellJobKeys.all, 'list'] });
    },
  });
}

/** Writes the returned job and refreshes its items + the recent list. */
function useJobAction(fn: (id: string) => Promise<ShellJob>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (job) => {
      queryClient.setQueryData<ShellJob>(shellJobKeys.detail(job.id), job);
      queryClient.invalidateQueries({ queryKey: shellJobKeys.items(job.id) });
      queryClient.invalidateQueries({ queryKey: [...shellJobKeys.all, 'list'] });
    },
  });
}

/** Other active runs of an item's device: the device panel's "Also in" row. */
export function useShellJobAlsoIn(jobId: string, itemId?: string) {
  return useQuery({
    queryKey: [...shellJobKeys.all, 'also-in', jobId, itemId],
    queryFn: () => shellJobService.alsoIn(jobId, itemId!),
    enabled: !!itemId,
    staleTime: 30_000,
  });
}

export const useResumeShellJob = () => useJobAction(shellJobService.resume);
export const useRetryFailedShellJob = () => useJobAction(shellJobService.retryFailed);
